/**
 * Robuste HTML-Druck-Hilfen (Browser + Electron-Routing).
 */

import { printSilentFast } from './printerUtils';

export async function waitForDocumentPrintReady(doc) {
  if (!doc) return;
  try {
    if (doc.fonts?.ready) await doc.fonts.ready;
  } catch {
    /* optional */
  }
  const images = [...(doc.images || [])];
  await Promise.all(
    images.map((img) =>
      img.complete
        ? Promise.resolve()
        : new Promise((resolve) => {
            img.onload = resolve;
            img.onerror = resolve;
          })
    )
  );
}

/**
 * Browser print with resource wait + afterprint before closing the window.
 */
export function printHtmlInBrowser(html, { windowFeatures = 'width=400,height=600' } = {}) {
  return new Promise((resolve) => {
    if (typeof window === 'undefined') {
      resolve({ success: false, error: 'no_window' });
      return;
    }

    const printWindow = window.open('', '_blank', windowFeatures);
    if (!printWindow) {
      resolve({
        success: false,
        error: 'popup_blocked',
        message: 'Popup blocked — allow popups to print'
      });
      return;
    }

    const finish = (success, extra = {}) => {
      try {
        if (!printWindow.closed) printWindow.close();
      } catch {
        /* ignore */
      }
      resolve({ success, method: 'browser', ...extra });
    };

    const runPrint = async () => {
      try {
        await waitForDocumentPrintReady(printWindow.document);
        printWindow.focus();

        const printed = await new Promise((res) => {
          const timeout = setTimeout(() => res(true), 120000);
          printWindow.onafterprint = () => {
            clearTimeout(timeout);
            res(true);
          };
          try {
            printWindow.print();
          } catch (err) {
            clearTimeout(timeout);
            res(false);
          }
        });

        finish(printed, printed ? {} : { error: 'print_cancelled' });
      } catch (err) {
        finish(false, { error: err?.message || 'browser_print_failed' });
      }
    };

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();

    if (printWindow.document.readyState === 'complete') {
      void runPrint();
    } else {
      printWindow.onload = () => {
        void runPrint();
      };
    }
  });
}

/**
 * Unified HTML print — Electron: always silent to role-mapped printer.
 * @param {string} html
 * @param {{
 *   margins?: string|object,
 *   kind?: 'thermal'|'document'|'label-sheet',
 *   role?: string,
 *   deviceName?: string,
 *   pageWidthMm?: number,
 *   pageHeightMm?: number,
 *   timeoutMs?: number,
 *   retries?: number,
 *   imageWaitMs?: number,
 *   windowFeatures?: string
 * }} [options]
 */
export async function printHtmlDocument(html, options = {}) {
  if (!html || !String(html).trim()) {
    return { success: false, error: 'empty_html' };
  }

  const isElectron = typeof window !== 'undefined' && window.electronAPI;

  if (isElectron) {
    return printSilentFast(html, {
      margins: options.margins,
      kind: options.kind,
      role: options.role,
      deviceName: options.deviceName,
      pageWidthMm: options.pageWidthMm,
      pageHeightMm: options.pageHeightMm,
      timeoutMs: options.timeoutMs,
      retries: options.retries,
      imageWaitMs: options.imageWaitMs
    });
  }

  return printHtmlInBrowser(html, {
    windowFeatures: options.windowFeatures || 'width=400,height=600'
  });
}
